import { Link } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import { BRAND_COLORS, SUBJECTS, type MeResponse, type ProgressResponse } from '@lk/shared';
import type { WalletResponse } from '../../lib/api';
import { Icon, SUBJECT_ICON } from '../../components/Icon';
import { Panel, Stat, Meter } from '../../components/Panel';
import { ErrorState, LoadingState } from '../../components/States';
import { daysLabel, formatPercent, hryvniaLabel, minutesLabel } from '../../lib/format';
import { progressQuery, walletQuery } from '../learner/queries';
import { useUiStore } from '../../store/uiStore';
import { useSelectedLearner } from './ParentLayout';
import { ProgressChart } from './ProgressChart';

export function OverviewPage({ me }: { me: MeResponse }): React.JSX.Element {
  const days = useUiStore((s) => s.parentDays);
  const setDays = useUiStore((s) => s.setParentDays);
  const learner = useSelectedLearner(me);

  const results = useQueries({
    queries: (learner ? [learner] : []).flatMap((l) => [progressQuery(l.id, days), walletQuery(l.id)]),
  });

  if (!learner) {
    return <ErrorState error={new Error('До акаунта не привʼязано жодної дитини.')} />;
  }
  if (results.some((r) => r.isLoading)) return <LoadingState label="Рахуємо статистику…" />;

  const firstError = results.find((r) => r.isError);
  if (firstError?.error) {
    return <ErrorState error={firstError.error} onRetry={() => results.forEach((r) => void r.refetch())} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-black tracking-widest text-white/45 uppercase">Період</span>
        {[7, 30, 90].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setDays(value)}
            aria-pressed={days === value}
            className={`lk-chip cursor-pointer ${days === value ? 'border-[var(--lk-gold)] text-[var(--lk-gold)]' : ''}`}
          >
            {daysLabel(value)}
          </button>
        ))}
      </div>

      {(() => {
        const progress = results[0]?.data as ProgressResponse | undefined;
        const wallet = results[1]?.data as WalletResponse | undefined;
        if (!progress || !wallet) return null;

        const totals = progress.daily.reduce(
          (acc, day) => ({
            attempts: acc.attempts + day.attempts,
            correct: acc.correct + day.correct,
            minutes: acc.minutes + day.minutes,
          }),
          { attempts: 0, correct: 0, minutes: 0 },
        );
        const accuracy = totals.attempts > 0 ? totals.correct / totals.attempts : 0;
        const weakSubjects = progress.subjects.filter((s) => s.isWeak);

        return (
          <Panel key={learner.id} className="p-5">
            <header className="flex flex-wrap items-baseline gap-3">
              <h2 className="text-2xl font-black">{learner.displayName}</h2>
              <span className="lk-chip text-[10px]">{learner.grade} клас</span>
              <div className="flex-1" />
              <Link to="/parent/skills" className="lk-chip text-[10px]">
                мапа навичок →
              </Link>
            </header>

            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat
                label="Влучність"
                value={formatPercent(accuracy)}
                hint={`${totals.correct} з ${totals.attempts}`}
                tone={BRAND_COLORS.green}
              />
              <Stat
                label="Час"
                value={minutesLabel(Math.round(totals.minutes))}
                hint={`за ${daysLabel(days)}`}
                tone={BRAND_COLORS.cyan}
              />
              <Stat
                label="Серія днів"
                value={progress.streakDays}
                hint="без пропусків"
                tone={BRAND_COLORS.gold}
              />
              <Stat
                label="Гаманець"
                value={hryvniaLabel(wallet.balance)}
                hint={`сьогодні ${wallet.earnedToday}/${wallet.dailyCap} · тиждень ${wallet.earnedThisWeek}`}
                tone={BRAND_COLORS.gold}
              />
            </div>

            <div className="mt-5">
              <h3 className="text-[11px] font-black tracking-widest text-white/45 uppercase">
                Прогрес у часі
              </h3>
              <ProgressChart daily={progress.daily} />
            </div>

            <div className="mt-5">
              <h3 className="text-[11px] font-black tracking-widest text-white/45 uppercase">
                Слабкі місця й розподіл питань
              </h3>
              <ul className="mt-2 flex flex-col gap-2">
                {progress.subjects.map((subject) => (
                  <li key={subject.subject} className="lk-panel-tight p-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-black">
                      <Icon name={SUBJECT_ICON[subject.subject]} size={18} />
                      <span>{SUBJECTS[subject.subject].title}</span>
                      {subject.isWeak ? (
                        <span className="lk-chip border-[var(--lk-red)] text-[10px] text-[var(--lk-red)]">
                          слабке місце
                        </span>
                      ) : null}
                      <div className="flex-1" />
                      <span className="tabular text-white/60">
                        {formatPercent(subject.accuracy)} влучність
                      </span>
                      <span
                        className="tabular"
                        style={{
                          color: subject.trend >= 0 ? BRAND_COLORS.green : BRAND_COLORS.red,
                        }}
                      >
                        {subject.trend >= 0 ? '▲' : '▼'} {Math.abs(subject.trend).toFixed(2)}
                      </span>
                    </div>
                    <Meter
                      className="mt-2"
                      value={subject.mastery}
                      label={`Опанування: ${SUBJECTS[subject.subject].title}`}
                      from={BRAND_COLORS.red}
                      to={BRAND_COLORS.green}
                    />
                    <p className="mt-1 text-[11px] font-bold text-white/50">
                      зараз {formatPercent(subject.currentWeight)} питань спрямовано сюди ·{' '}
                      {subject.attempts} спроб
                    </p>
                  </li>
                ))}
              </ul>
              {weakSubjects.length > 0 ? (
                <p className="mt-2 text-xs font-bold text-white/55">
                  Система вже підняла вагу цих предметів і зробила їх найвигіднішими за балами.
                </p>
              ) : null}
            </div>
          </Panel>
        );
      })()}
    </div>
  );
}
